import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import 'bloko/blocks/treeSelector/treeSelector.less';
import dataQa from 'bloko/blocks/treeSelector/dataQa';
import TreeSelectorElement from 'bloko/blocks/treeSelector/treeSelectorElement';
import TreeCollection from 'bloko/common/tree/treeCollection';
import fuzzySearch from 'bloko/common/fuzzySearch';

/**
 * Возвращает массив ID элементов дерева, у которых есть и выбранные, и невыбранные потомки.
 * @param {TreeCollection} collection
 * @param {Array} selected
 * @returns {Array}
 */
function getIndeterminateParentIds(collection, selected) {
    const result = [];
    selected.forEach((id) => {
        collection.getParentIds(id).forEach((parentId) => {
            if (!selected.includes(parentId)) {
                result.push(parentId);
            }
        });
    });
    return result;
}

let inputNameCounter = 0;
function getUniqueInputName() {
    const name = `bloko-tree-selector-default-name-${inputNameCounter}`;
    inputNameCounter += 1;
    return name;
}

function needToApply(query) {
    return query && query.length;
}

function trim(string) {
    return string ? string.trim() : string;
}

/**
 * Управляемый компонент для работы с древовидной структурой данных.
 * Получает экземпляр `TreeCollection`, `selected` и `disabled`.
 * Обрабатывает сам `expanded` и `indeterminate`, всё остальное
 * только отрисовывает. Если не требуется ручное управление,
 * см. умный компонент <a href="#treeselector">TreeSelector</a>.
 */
export default class TreeSelectorDummy extends PureComponent {
    state = {
        collection: this.props.collection,
        isServerENV: true,
        inputName: this.props.inputName || getUniqueInputName(),
        expanded: this.props.expanded ? this.props.expanded.slice() : this.props.initialExpanded.slice(),
        indeterminate: [],
    };

    updateCollectionAndExpanded(prevProps = {}, prevState = {}) {
        const {
            contentFilterQuery,
            onExpand,
            collection,
            selected,
            initialExpanded,
            expanded,
            onChangeFilterQuery,
        } = this.props;
        const contentFilterQueryTrimmed = trim(contentFilterQuery);
        const queryWasChanged = contentFilterQueryTrimmed !== trim(prevProps.contentFilterQuery);

        let newCollection = collection;
        let newExpanded = this.state.expanded;

        if (queryWasChanged && needToApply(contentFilterQueryTrimmed)) {
            newExpanded = [];
            newCollection = collection.filter((item) => fuzzySearch.match(contentFilterQueryTrimmed, item.text));
            newCollection.toList().forEach((item) => {
                if (newCollection.hasChildren(item.id)) {
                    // Если есть в отфильтрованной коллекции есть дочерние элементы,
                    // значит они заматчились, и нужно открыть родителя.
                    if (queryWasChanged) {
                        newExpanded.push(item.id);
                    }
                } else {
                    // Если заматчился только сам родитель, показываем его
                    // схлопнутым => нужно добавить его ветку в коллекцию.
                    collection.walkChildren(item.id, (child, parents) => {
                        newCollection.addModel(child, parents[0].id);
                    });
                }
            });

            this.setState({
                collection: newCollection,
                indeterminate: getIndeterminateParentIds(collection, selected),
            });
        } else if (queryWasChanged) {
            // Запрос не годится для поиска.
            newExpanded = initialExpanded.slice();
        }

        if (expanded && expanded !== prevProps.expanded) {
            newExpanded = expanded.slice();
        }

        if (prevState.expanded !== newExpanded) {
            if (onExpand) {
                onExpand(newExpanded.slice());
            }
            this.setState({ expanded: newExpanded });
        }

        if (queryWasChanged) {
            const filteredIds = [];
            newCollection.walk((item) => {
                return filteredIds.push(item.id);
            });
            onChangeFilterQuery && onChangeFilterQuery(filteredIds.slice());
        }
    }

    componentDidMount() {
        this.setState({ isServerENV: false });
        this.updateCollectionAndExpanded();
    }

    componentDidUpdate(prevProps, prevState) {
        this.updateCollectionAndExpanded(prevProps, prevState);
    }

    handleExpansion = (id) => {
        const { expanded: previousExpanded } = this.state;
        let expanded;
        if (previousExpanded.includes(id)) {
            expanded = previousExpanded.filter((itemId) => itemId !== id);
        } else {
            expanded = previousExpanded.slice();
            expanded.push(id);
        }
        this.setState({ expanded });
    };

    getClassName(hasAction, hasChildren, isRoot) {
        const hanging = hasChildren && !isRoot;
        return classnames('bloko-tree-selector-item', {
            'bloko-tree-selector-item_without-action': !hasAction,
            'bloko-tree-selector-item_has-parent': hasAction && hanging,
            'bloko-tree-selector-item_without-action-has-parent': !hasAction && hanging,
        });
    }

    renderList(items) {
        const { collection, expanded, indeterminate, inputName } = this.state;
        const { singleChoice, leavesOnly, selected, disabled, LabelComponent, onChange } = this.props;

        return items.map((item) => {
            const isExpanded = expanded.includes(item.id);
            const hasChildren = collection.hasChildren(item.id);
            const hasAction = !(leavesOnly && hasChildren);
            const isRoot = !collection.getParentId(item.id);
            const className = this.getClassName(hasAction, hasChildren, isRoot);
            return (
                <div
                    key={item.id}
                    className={className}
                    data-qa={classnames(dataQa.item, `${dataQa.item}-${item.id}`, { [dataQa.expanded]: isExpanded })}
                >
                    <div className="bloko-form-item">
                        <TreeSelectorElement
                            id={item.id}
                            name={inputName}
                            hasAction={hasAction}
                            hasChildren={hasChildren}
                            disabled={disabled.includes(item.id)}
                            selected={selected.includes(item.id)}
                            expanded={isExpanded}
                            indeterminate={indeterminate.includes(item.id)}
                            onExpansion={this.handleExpansion}
                            onChange={onChange}
                            singleChoice={singleChoice}
                            leavesOnly={leavesOnly}
                        >
                            {LabelComponent ? (
                                <LabelComponent id={item.id} text={item.text} additional={item.additional} />
                            ) : (
                                item.text
                            )}
                        </TreeSelectorElement>
                    </div>
                    {hasChildren &&
                        isExpanded && (
                            <div className="bloko-tree-selector__items" data-qa={dataQa.items}>
                                {this.renderList(collection.getChildren(item.id))}
                            </div>
                        )}
                </div>
            );
        });
    }

    render() {
        if (this.state.isServerENV) {
            return null;
        }

        const { collection } = this.state;
        return <div>{this.renderList(collection.getTopLevel())}</div>;
    }

    static propTypes = {
        /** Имя инпута. */
        inputName: PropTypes.string,
        /** Дерево элементов. */
        collection: PropTypes.instanceOf(TreeCollection).isRequired,
        /** Разрешает выбор только элементов без потомков. */
        leavesOnly: PropTypes.bool,
        /** Разрешает выбор только одного элемента. */
        singleChoice: PropTypes.bool,
        /** Выбранные ID. */
        selected: PropTypes.arrayOf(PropTypes.string),
        /** Изначально открытые ID. Применяются только при инициализации. */
        initialExpanded: PropTypes.arrayOf(PropTypes.string),
        /** Изначально открытые ID, которые можно динамически изменять извне. */
        expanded: PropTypes.arrayOf(PropTypes.string),
        /** Запрещённые ID. */
        disabled: PropTypes.arrayOf(PropTypes.string),
        /** Компонент для вывода метки */
        LabelComponent: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.instanceOf(React.Component),
            PropTypes.instanceOf(React.PureComponent),
        ]),
        /** Обработчик изменения набора выбранных ID.
         * В аргументах получает `{String} id`, `{Boolean} isSelected`. */
        onChange: PropTypes.func,
        /** Обработчик изменения состояния раскрытия элементов
         * В аргументах получает список id раскрытых элементов `{Array} expanded` */
        onExpand: PropTypes.func,
        /** Фильтр для дерева, остаются только элементы со вхождением указанной строки. */
        contentFilterQuery: PropTypes.string,
        /** Обработчик изменения строки contentFilterQuery
         * В аргументах получает список подходящих под запрос id */
        onChangeFilterQuery: PropTypes.func,
    };

    static defaultProps = {
        leavesOnly: false,
        singleChoice: false,
        selected: [],
        initialExpanded: [],
        disabled: [],
        onChange: () => {},
        contentFilterQuery: null,
    };
}
